import { X } from 'lucide-react'
import React, { useEffect, useState, useRef } from 'react'

import { cn } from 'src/shared/utils/cn'

import Portal from '../utils/portals/portal'

interface BottomSheetProps {
  open: boolean
  close: () => void
  children: React.ReactNode
  maxHeight?: string
  showCloseButton?: boolean
  backdropOpacity?: number
  className?: string
}

export const BottomSheet = ({
  open,
  close,
  children,
  maxHeight = '90vh',
  showCloseButton = true,
  backdropOpacity = 0.5,
  className,
}: BottomSheetProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setIsVisible(true)

      // Use a longer delay to ensure DOM is fully ready
      setTimeout(() => {
        if (sheetRef.current) {
          sheetRef.current.style.transform = 'translateY(0)'
        }
        if (backdropRef.current) {
          backdropRef.current.style.opacity = backdropOpacity.toString()
        }
      }, 10)
    } else {
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(100%)'
      }
      if (backdropRef.current) {
        backdropRef.current.style.opacity = '0'
      }
      // Wait for animation to complete before hiding
      setTimeout(() => {
        setIsVisible(false)
      }, 300)
    }
  }, [open, backdropOpacity])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      close()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      close()
    }
  }

  if (!isVisible) {
    return null
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end justify-center" tabIndex={-1} onKeyDown={handleKeyDown}>
        {/* Backdrop */}
        <div
          ref={backdropRef}
          className="absolute inset-0 bg-black transition-opacity duration-300 ease-out"
          style={{ opacity: 0 }}
          onClick={handleBackdropClick}
        />

        {/* Bottom Sheet */}
        <div
          ref={sheetRef}
          className={cn(
            'relative w-full rounded-t-[30px] bg-white shadow-2xl transition-transform duration-300 ease-out',
            'max-h-[90vh] overflow-y-auto',
            className,
          )}
          style={{
            transform: 'translateY(100%)',
            maxHeight,
          }}
        >
          {/* Header with Close Button - Fixed at top */}
          <div className="sticky top-0 z-10 flex items-center justify-end rounded-t-[30px] bg-white p-3 pb-0">
            {showCloseButton && (
              <button onClick={close} className="p-2 transition-colors" aria-label="Close">
                <X className="size-5 text-primary" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="px-5 pb-10">{children}</div>
        </div>
      </div>
    </Portal>
  )
}
